import { Box } from '@primer/react';
import { MaterialityType } from '@upright/common/model/input/materiality-assessment/types';
import { SustainabilityMatterId } from '@upright/common/model/input/materiality-assessment/types-matters';
import { InfoGlyph } from '@upright/ui/common/generic/components/tooltip';
import styled, { css } from 'styled-components';
import {
  MaterialityMatrixLevel,
  MatrixMatter,
} from '../../../../../../../../common/model/output/materiality/materiality-matrix/types';
import { compareMatrixMattersInCell } from '@upright/common/model/output/materiality/materiality-matrix/utils';
import { SingleMatrixMatter } from './single-matrix-matter';
import { borderCSS, borderRadiusCSS } from './style';

export function MatterBox(props: {
  imLevel: MaterialityMatrixLevel;
  fmLevel: MaterialityMatrixLevel;
  matters: MatrixMatter[];
  getSingleMatterViewUrl?: (matterId: SustainabilityMatterId, materialityType: MaterialityType) => string;
}) {
  const { imLevel, fmLevel, matters, getSingleMatterViewUrl } = props;

  const x = fmLevel;
  const y = imLevel;

  if (imLevel === 'low' && fmLevel === 'low') {
    return (
      <MatterBoxRoot x={x} y={y}>
        <LowLowBox count={matters.length} />
      </MatterBoxRoot>
    );
  }

  return (
    <MatterBoxRoot x={x} y={y}>
      {matters.sort(compareMatrixMattersInCell).map(matter => {
        return (
          <SingleMatrixMatter
            key={`${x}${y}${matter.matterId}`}
            matterId={matter.matterId}
            valence={getValenceOfMatrixMatter(matter)}
            getSingleMatterViewUrl={getSingleMatterViewUrl}
          />
        );
      })}
    </MatterBoxRoot>
  );
}

const MatterBoxRoot = styled.div<{ x: MaterialityMatrixLevel; y: MaterialityMatrixLevel }>`
  min-width: 220px;
  background-color: ${props => getMatterBoxBackground(props.x, props.y)};
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 6px;

  grid-column: ${props => `x-${props.x}`};
  grid-row: ${props => `y-${props.y}`};

  border-bottom: ${borderCSS};
  border-right: ${borderCSS};
  // First row and column draw the missing border
  ${props =>
    props.x === 'low'
      ? css`
          border-left: ${borderCSS};
        `
      : ''}
  ${props =>
    props.y === 'high'
      ? css`
          border-top: ${borderCSS};
        `
      : ''}
  // Round all four corners
  ${props =>
    props.y === 'high' && props.x === 'low'
      ? css`
          border-top-left-radius: ${borderRadiusCSS};
        `
      : ''}
  ${props =>
    props.y === 'high' && props.x === 'high'
      ? css`
          border-top-right-radius: ${borderRadiusCSS};
        `
      : ''}
  ${props =>
    props.y === 'low' && props.x === 'low'
      ? css`
          border-bottom-left-radius: ${borderRadiusCSS};
        `
      : ''}
  ${props =>
    props.y === 'low' && props.x === 'high'
      ? css`
          border-bottom-right-radius: ${borderRadiusCSS};
        `
      : ''}
`;

function getMatterBoxBackground(x: MaterialityMatrixLevel, y: MaterialityMatrixLevel) {
  const scale = matrixLevelToNumber(x) + matrixLevelToNumber(y);
  return colors[scale];
}
// From low materiality to highest
const colors = ['#F9FAFB', '#F4FAFF', '#F2F8FF', '#E9F5FE', '#D4EAFE'];

function matrixLevelToNumber(matrixLevel: MaterialityMatrixLevel) {
  switch (matrixLevel) {
    case 'high':
      return 2;
    case 'medium':
      return 1;
    case 'low':
      return 0;
  }
}

function LowLowBox(props: { count: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', height: '100%', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box sx={{ height: '1em' }}>
          <InfoIconRoot />
        </Box>
        <Box>{props.count} matters with low or no materiality</Box>
      </Box>
    </Box>
  );
}

function getValenceOfMatrixMatter(matter: MatrixMatter) {
  const { positive, negative } = matter;
  if (positive && negative) {
    return 'posneg';
  }
  if (positive) return 'pos';
  if (negative) return 'neg';
  return null;
}

const InfoIconRoot = styled(InfoGlyph).attrs({ width: 12 })`
  color: var(--fgColor-muted);
`;
